// useVoiceLive — a live voice-to-voice practice session against the backend's
// WS /voice/live (GPT-Realtime speech-to-speech). Click-to-start opens the
// socket, streams the mic as 24 kHz PCM16, and plays back the coach's 24 kHz
// PCM16 reply. Control events carry live transcripts plus the coaching signals
// (focus phrase, phrase practised, corrections, the end-of-session recap).
//
// Server-VAD segments the turns, so the browser only sends audio — no
// push-to-talk. Echo cancellation matters: without it the mic re-hears the
// coach through the speakers and the model interrupts itself.

import { useCallback, useEffect, useRef, useState } from 'react';
import { TOKEN_KEY } from '../services/api';
import { fetchHealth } from '../services/englishApi';

// The coach socket is served by this same origin, so the host comes from the
// page rather than from configuration: http -> ws, https -> wss.
const WS_BASE_URL = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/english`;

const RATE = 24000; // OpenAI Realtime ingests/emits 24 kHz PCM16 mono.

// Float32 [-1,1] @ srcRate -> Int16 LE bytes @ RATE (linear resample).
function floatToPcm16(input, srcRate) {
  const ratio = RATE / srcRate;
  const outLen = Math.max(1, Math.floor(input.length * ratio));
  const out = new Int16Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const srcPos = i / ratio;
    const lo = Math.floor(srcPos);
    const frac = srcPos - lo;
    const a = input[lo] ?? 0;
    const b = input[lo + 1] ?? a;
    let s = a + (b - a) * frac;
    s = Math.max(-1, Math.min(1, s));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out.buffer;
}

export default function useVoiceLive({
  slug,
  onCoverage,
  onFocus,
  onPracticed,
  onCorrection,
  onTurn,
  onSummary,
  onNotice,
  onError,
} = {}) {
  const [status, setStatus] = useState('idle'); // idle|connecting|listening|speaking|error
  const [userPartial, setUserPartial] = useState('');
  const [coachPartial, setCoachPartial] = useState('');
  const [elapsed, setElapsed] = useState(0); // seconds — Realtime bills per minute
  const [sessionCost, setSessionCost] = useState(null); // USD so far, streamed

  const wsRef = useRef(null);
  const micCtxRef = useRef(null);
  const streamRef = useRef(null);
  const nodesRef = useRef([]);
  const playCtxRef = useRef(null);
  const playHeadRef = useRef(0); // scheduled-audio cursor (playback ctx time)
  const playingRef = useRef([]); // scheduled source nodes (for barge-in flush)
  const startedAtRef = useRef(0);
  // Did this socket ever reach OPEN? A handshake refusal and a mid-session drop
  // arrive at onclose looking identical otherwise.
  const openedRef = useRef(false);

  // Latest callbacks without re-creating start/stop on every render.
  const cbRef = useRef({});
  useEffect(() => {
    cbRef.current = {
      onCoverage, onFocus, onPracticed, onCorrection, onTurn, onSummary, onNotice, onError,
    };
  });

  // Accumulate transcript deltas per turn.
  const acc = useRef({ user: '', coach: '' });
  // Is a turn currently open? The server holds a turn open for a late transcript,
  // but if that grace period expires the transcript can still arrive afterwards —
  // in which case it belongs on screen as its own bubble rather than being
  // dropped or silently glued to the front of the next turn.
  const turnOpen = useRef(false);

  const teardownAudio = useCallback(() => {
    nodesRef.current.forEach((n) => {
      try { n.disconnect(); } catch { /* already gone */ }
    });
    nodesRef.current = [];
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (micCtxRef.current) {
      try { micCtxRef.current.close(); } catch { /* already closed */ }
      micCtxRef.current = null;
    }
    if (playCtxRef.current) {
      try { playCtxRef.current.close(); } catch { /* already closed */ }
      playCtxRef.current = null;
    }
    playingRef.current = [];
    playHeadRef.current = 0;
  }, []);

  const stop = useCallback(() => {
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) {
      try { ws.close(); } catch { /* ignore */ }
    }
    teardownAudio();
    setStatus('idle');
    setUserPartial('');
    setCoachPartial('');
    // Deliberately NOT clearing sessionCost: the last session's spend should
    // stay on screen after Stop, not blink back to nothing.
    acc.current = { user: '', coach: '' };
    turnOpen.current = false;
  }, [teardownAudio]);

  // Flush any scheduled playback (server-VAD barge-in).
  const flushPlayback = useCallback(() => {
    playingRef.current.forEach((src) => {
      try { src.stop(); } catch { /* already stopped */ }
    });
    playingRef.current = [];
    if (playCtxRef.current) playHeadRef.current = playCtxRef.current.currentTime;
  }, []);

  // Schedule one PCM16 chunk (ArrayBuffer) back-to-back on the playback context.
  const playChunk = useCallback((arrayBuffer) => {
    const ctx = playCtxRef.current;
    if (!ctx) return;
    const pcm = new Int16Array(arrayBuffer);
    if (!pcm.length) return;
    const buf = ctx.createBuffer(1, pcm.length, RATE);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < pcm.length; i++) ch[i] = pcm[i] / 0x8000;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    const startAt = Math.max(ctx.currentTime, playHeadRef.current);
    src.start(startAt);
    playHeadRef.current = startAt + buf.duration;
    playingRef.current.push(src);
    src.onended = () => {
      playingRef.current = playingRef.current.filter((s) => s !== src);
    };
  }, []);

  const handleControl = useCallback((msg) => {
    switch (msg.type) {
      case 'session_ready':
        setStatus('listening');
        break;
      case 'input_transcript':
        turnOpen.current = true;
        acc.current.user += msg.text || '';
        setUserPartial(acc.current.user);
        break;
      case 'cost':
        // Spend streams in as each coach turn completes.
        setSessionCost(msg.total_usd);
        break;
      case 'input_transcript_dropped':
        // The server judged the last transcript a hallucination and removed it
        // from the conversation — clear the partial so it never shows as a turn.
        acc.current.user = '';
        setUserPartial('');
        break;
      case 'input_transcript_done':
        if (msg.text) {
          if (turnOpen.current) {
            acc.current.user = msg.text;
            setUserPartial(msg.text);
          } else {
            // Arrived after its turn already closed — emit it standalone so the
            // student's words never vanish from the transcript.
            cbRef.current.onTurn?.({ role: 'user', text: msg.text });
          }
        }
        break;
      case 'output_transcript':
        turnOpen.current = true;
        setStatus('speaking');
        acc.current.coach += msg.text || '';
        setCoachPartial(acc.current.coach);
        break;
      case 'coverage':
        cbRef.current.onCoverage?.(msg);
        break;
      case 'focus':
        cbRef.current.onFocus?.(msg.phrase);
        break;
      case 'practiced':
        cbRef.current.onPracticed?.(msg);
        break;
      case 'correction':
        cbRef.current.onCorrection?.(msg);
        break;
      case 'session_summary':
        cbRef.current.onSummary?.(msg);
        break;
      case 'interrupted':
        flushPlayback();
        break;
      case 'resumed':
        // The backend reconnected to Gemini mid-session (the Live API closes a
        // connection after ~10 minutes). The conversation continues; say so
        // rather than leaving a second of silence looking like a freeze.
        cbRef.current.onNotice?.('Reconnected — carry on, the coach still has the thread.');
        break;
      case 'turn_complete': {
        turnOpen.current = false;
        const u = acc.current.user.trim();
        const c = acc.current.coach.trim();
        if (u) cbRef.current.onTurn?.({ role: 'user', text: u });
        if (c) cbRef.current.onTurn?.({ role: 'coach', text: c });
        acc.current = { user: '', coach: '' };
        setUserPartial('');
        setCoachPartial('');
        setStatus('listening');
        break;
      }
      case 'end':
        stop();
        break;
      case 'error':
        cbRef.current.onError?.(msg.detail || 'Voice session error.');
        setStatus('error');
        break;
      default:
        break;
    }
  }, [flushPlayback, stop]);

  const start = useCallback(async () => {
    if (wsRef.current || !slug) return;
    setStatus('connecting');
    setSessionCost(null); // previous session's figure clears as this one starts

    // 1) Mic capture graph: source -> processor (PCM16 out) -> muted gain -> out.
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch {
      cbRef.current.onError?.('Mic access blocked — enable microphone permission.');
      setStatus('error');
      return;
    }
    streamRef.current = stream;
    const micCtx = new (window.AudioContext || window.webkitAudioContext)();
    micCtxRef.current = micCtx;
    const source = micCtx.createMediaStreamSource(stream);
    const processor = micCtx.createScriptProcessor(4096, 1, 1);
    const mute = micCtx.createGain();
    mute.gain.value = 0;
    processor.onaudioprocess = (e) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(floatToPcm16(e.inputBuffer.getChannelData(0), micCtx.sampleRate));
    };
    source.connect(processor);
    processor.connect(mute);
    mute.connect(micCtx.destination);
    nodesRef.current = [source, processor, mute];

    // 2) Dedicated 24 kHz playback context for the coach's spoken reply.
    const PlayCtx = window.AudioContext || window.webkitAudioContext;
    let playCtx;
    try {
      playCtx = new PlayCtx({ sampleRate: RATE });
    } catch {
      playCtx = new PlayCtx();
    }
    playCtxRef.current = playCtx;
    playHeadRef.current = playCtx.currentTime;

    // 3) The socket.
    const params = new URLSearchParams({ slug });
    // The admin JWT travels in the query string: a browser cannot set headers on
    // a WebSocket, so `Authorization` is not available at handshake.
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) params.set('token', token);
    const ws = new WebSocket(`${WS_BASE_URL}/voice/live?${params}`);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;
    openedRef.current = false;
    ws.onopen = () => { openedRef.current = true; };
    startedAtRef.current = Date.now();
    setElapsed(0);
    ws.onmessage = (e) => {
      if (typeof e.data === 'string') {
        try { handleControl(JSON.parse(e.data)); } catch { /* ignore junk */ }
      } else {
        playChunk(e.data);
      }
    };
    ws.onerror = () => {
      cbRef.current.onError?.('Voice connection error — is the backend running?');
      setStatus('error');
    };
    ws.onclose = (e) => {
      // A socket that never opened was refused at the handshake, which is what
      // the backend's auth check looks like from here: closing before accept()
      // surfaces as an HTTP 403 and a generic 1006, NOT as the 4401 the server
      // passed to close(). The browser never sees that code. So ask the REST
      // API who we are — its 401 interceptor clears the token and routes to
      // /signin — and only claim a connection problem if the token is fine.
      if (!openedRef.current) {
        cbRef.current.onError?.('The coach refused the connection — checking your session…');
        fetchHealth()
          .then(() =>
            cbRef.current.onError?.(
              'Could not open the voice connection. The backend is up, so this is the socket itself — check the browser console.',
            ),
          )
          .catch(() => {
            /* 401 -> the interceptor already redirected to /signin */
          });
        setStatus('error');
        if (wsRef.current === ws) stop();
        return;
      }
      if (e.code === 4404) cbRef.current.onError?.('That story was not found on the backend.');
      if (e.code === 4402) cbRef.current.onError?.(e.reason || 'GEMINI_API_KEY is not configured.');
      if (wsRef.current === ws) stop();
    };
  }, [slug, handleControl, playChunk, stop]);

  // Ask the coach to wrap up and give its recap (it calls finish_session).
  const finish = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'finish' }));
  }, []);

  // Type instead of speak — handy for a phrase that's hard to pronounce yet.
  const nudge = useCallback((text) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !text.trim()) return;
    ws.send(JSON.stringify({ type: 'nudge', text }));
  }, []);

  const active = status !== 'idle' && status !== 'error';

  // Session clock — the Realtime API bills per audio minute, so it stays visible.
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(
      () => setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000)),
      1000,
    );
    return () => clearInterval(id);
  }, [active]);

  // Tear down on unmount.
  useEffect(() => () => stop(), [stop]);

  return {
    status, active, userPartial, coachPartial, elapsed, sessionCost,
    start, stop, finish, nudge,
  };
}
